import { useEffect, useState } from "react";

import { useQuery } from "react-admin";

import { Employee } from "types/employees";

import dataProvider from "dataProvider";
import { httpClient } from "dataProvider";

import { SUPPLIER_NAME_ASWO } from "constants/supplier";

import { getResourceRoute } from "utils/routing";
import { sortSkus } from "utils/sku";

import { getHeaders } from "./headers";

export const useWorkshopFileTags = () => {
  const { data: workshopFileTags, loading: loadingWorkshopFileTags } = useQuery({
    type: "getList",
    resource: "workshop-file-tags",
    payload: {
      sort: { field: "text", order: "DESC" },
    },
  });
  return { workshopFileTags, loadingWorkshopFileTags };
};

export const useSkuSuppliers = () => {
  const { data: SupplierChoices } = useQuery({
    type: "getList",
    resource: "sku-suppliers",
    payload: {
      sort: { field: "id", order: "DESC" },
    },
  });
  return SupplierChoices;
};

export const useAswoSupplier = () => {
  const { data: aswoSupplier } = useQuery({
    type: "getOne",
    resource: "sku-suppliers",
    payload: {
      id: SUPPLIER_NAME_ASWO,
    },
  });
  return aswoSupplier;
};

export const getAswoSupplier = () => {
  return dataProvider.getOne("sku-suppliers", { id: SUPPLIER_NAME_ASWO } as any);
};

export const useWorkshopFiles = (filter?: Record<string, any>) => {
  const { data: workshopFiles } = useQuery({
    type: "getList",
    resource: "workshop-files",
    payload: {
      sort: { field: "id", order: "DESC" },
      filter: filter,
    },
  });
  return workshopFiles;
};

export const useWorkshopFile = (id: number | string) => {
  const { data: workshopFile } = useQuery({
    type: "getOne",
    resource: "workshop-files",
    payload: { id: id },
  });
  return workshopFile;
};

export const useWorkshopFilesCount = (filter?: Record<string, any>) => {
  const { data: count } = useQuery({
    type: "getList",
    resource: "workshop-files-count",
    payload: { sort: { field: "id", order: "DESC" }, filter },
  });
  return count;
};

export const useProducts = (filter?: Record<string, any>) => {
  const { data: products } = useQuery({
    type: "getList",
    resource: "products",
    payload: {
      sort: { field: "id", order: "DESC" },
      filter: filter,
    },
  });
  return products;
};

export const useProductModels = (filter?: Record<string, any>) => {
  const { data: productModels } = useQuery({
    type: "getList",
    resource: "product-models",
    payload: {
      filter: filter,
      sort: {},
    },
  });
  return productModels;
};

export const getProductModel = (productModelId: number | string) => {
  return dataProvider.getOne("product-models", { id: productModelId } as any).then(({ data }: any) => data);
};

export const useProductTypes = (filter?: Record<string, any>) => {
  const { data: productTypes } = useQuery({
    type: "getList",
    resource: "product-types",
    payload: {
      sort: { field: "id", order: "DESC" },
      filter: filter,
    },
  });
  return productTypes;
};

export const useProductTypeAttributes = (filter?: Record<string, any>) => {
  const { data: productTypes } = useQuery({
    type: "getList",
    resource: "product-type-attributes",
    payload: {
      sort: { field: "id", order: "DESC" },
      filter: filter,
    },
  });
  return productTypes;
};

export const useProductBrands = () => {
  const { data: brands } = useQuery({
    type: "getList",
    resource: "product-brands",
    payload: {
      sort: {},
    },
  });
  return brands;
};

export const useDeliveryOptions = (postalCode: string, store: string) => {
  const { data: options } = useQuery({
    type: "getList",
    resource: "delivery-options",
    payload: {
      sort: {},
      postal_code: postalCode,
      filter: { store: store },
    },
  });
  return options;
};

export function useWarranties(price: number, product_id: number | string) {
  /* As Warranties do not change often, enable 15 minutes cache*/
  const { data: warranties } = useQuery({
    type: "getList",
    resource: "warranty",
    payload: {
      sort: {},
      filter: { product_price: price, product_id: product_id },
    },
  });
  return warranties;
}

export const getSellingPrice = (
  reference: string,
  supplier: string,
  type: string = "new",
  designation: string = ""
) => {
  return dataProvider.getList("sku-selling-price", {
    pagination: { page: 1, perPage: 100 },
    sort: {},
    filter: { reference: reference, supplier: supplier, type: type, designation: designation },
  } as any);
};

export const useSkuSellingPrice = (skuId: number | string) => {
  const { data } = useQuery({ type: "getOne", resource: `sku/skus/${skuId}/selling-price`, payload: {} });
  return data ? data.selling_price : null;
};

export const getSkuSellingPrice = (skuId: number | string) => {
  return dataProvider
    .getOne(`sku/skus/${skuId}/selling-price`, {} as any)
    .then(({ data }: any) => (data ? data.selling_price : null));
};

export const createHistorySkuPrice = (skuId: number | string, sellingPrice: number) => {
  const SUPPORT_INPUT = "support_input";
  dataProvider.create("sku-price-history", {
    data: {
      sku: skuId,
      selling_price: sellingPrice * 100,
      strategy: SUPPORT_INPUT,
    },
  } as any);
};

export const createProductModel = (data: {
  brand: { id: number | string };
  reference: string;
  product_type: { id: number | string };
}) => {
  return dataProvider.create("product-models", {
    data: {
      brand: data.brand.id,
      reference: data.reference,
      product_type: data.product_type.id,
    },
  } as any);
};

export const updateProductModelGtin = (data: { id?: number | string; gtin?: string | null }) => {
  return dataProvider.update("product-models", {
    id: data.id,
    data: { gtin: data.gtin || null },
  } as any);
};

export const useLocations = (filter?: Record<string, any>) => {
  const { data: locations } = useQuery({
    type: "getList",
    resource: "location",
    payload: {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "id", order: "DESC" },
      filter: { ...filter },
    },
  });
  return locations;
};

export const useSkuLocations = () => {
  const { data: skuLocations } = useQuery({
    type: "getList",
    resource: "sku-location",
    payload: {
      sort: { field: "id", order: "DESC" },
    },
  });
  return skuLocations;
};

export const getAswoSparePart = async (aswoReference: string) => {
  const {
    data: aswoSparePart,
    loading,
    error,
  }: any = await dataProvider.getOne("murfy-erp/aswo-piece", {
    id: aswoReference,
  } as any);
  return { aswoSparePart, loading, error };
};

export const useAswoArticle = (reference: string) => {
  const { data: aswoArticle } = useQuery({
    type: "getOne",
    resource: "murfy-erp/aswo-piece",
    payload: { id: reference },
  });
  return aswoArticle;
};

export const useAswoPrice = (reference: string) => {
  const aswoArticle = useAswoArticle(reference);
  return aswoArticle && aswoArticle.Stueckpreis ? (parseFloat(aswoArticle.Stueckpreis) * 100).toFixed(0) : null;
};

export const useConnectedEmployee = () => {
  const { data: employee } = useQuery({
    type: "getOne",
    resource: "account",
    payload: {},
  });
  return employee;
};

export const getEmployees = async (filter?: Record<string, any>) => {
  const { data: employees } = await dataProvider.getList("employees", {
    sort: {},
    filter: filter,
  } as any);
  return employees;
};

export const useEmployees = (): Employee[] => {
  const { data: employees } = useQuery({
    type: "getList",
    resource: "employees",
    payload: {
      sort: { field: "id", order: "DESC" },
    },
  });
  return employees;
};

export const useWorkshops = () => {
  const { data: workshops } = useQuery({
    type: "getList",
    resource: "workshop",
    payload: {
      sort: { field: "id", order: "DESC" },
    },
  });
  return workshops;
};
export const getQuotes = (customerFileId: number | string) => {
  return dataProvider.getManyReference("billing-quotes", {
    target: "customer_file_id",
    id: customerFileId,
    pagination: {},
    sort: { field: "version", order: "DESC" },
  } as any);
};

export const postQuote = (payload: Record<string, any>) => {
  return dataProvider.create("payment/quotebilling", {
    data: payload,
  } as any);
};

export const sendQuoteSubmissionEmail = (payload: Record<string, any>) => {
  return dataProvider.create("send-quote-submission-email-v2", {
    data: payload,
  } as any);
};

export const updateQuote = (id: number | string, payload: Record<string, any>) => {
  return dataProvider.update("billing-detail", { id: id, data: payload } as any);
};

export const postDiscountRepairBonus = (id: number | string, product_id: number | string) => {
  let url = `${process.env.REACT_APP_API_URL}/payment/quotebilling/${id}/discount-repair-bonus/`;
  const options = { method: "POST", body: JSON.stringify({ product_id: product_id }) };
  return httpClient(url, options as any)
    .then((response: any) => convertHttpResponse(response))
    .catch((error: any) => convertHttpError(error));
};

const convertHttpResponse = (response: any) => {
  const { headers, json, status, body } = response;
  return {
    data: json || body,
    status,
    headers: headers,
  };
};

const convertHttpError = ({ body, status }: { body: any; status: number }) => ({ data: body, status });

export const postPayment = (payload: Record<string, any>) => {
  return dataProvider.create("payment/paymentbilling", { data: payload } as any);
};

export const createComment = (payload: Record<string, any>) => {
  return dataProvider.create("comments", {
    data: payload,
  } as any);
};

export const updateComment = (commentId: number | string, payload: Record<string, any>) => {
  return dataProvider.update("comments", {
    id: commentId,
    data: payload,
  } as any);
};

export const updateAttributeValue = (payload: Record<string, any>) => {
  return dataProvider.create("product/base-attribute-values/update-value", { data: payload } as any);
};

export const createCommentAttachments = (payload: Record<string, any>) => {
  return dataProvider.create("comment-attachments", {
    data: payload,
  } as any);
};

export const searchSkuStock = (reference: string) => {
  const url = `${process.env.REACT_APP_API_URL}/${getResourceRoute("sku-search")}/?reference=${encodeURIComponent(
    reference
  )}`;
  return httpClient(url).then(({ json }: any) => json);
};

const MURFY_LISTING_SUPPLIER_NAMES = ["MURFY_NEW", "MURFY_SECOND_HAND"];

const buildSkuFromMurfyListing = (searchResult: any, listing: any) => ({
  id: listing.murfy_id,
  type: listing.type,
  stock_available: listing.stock_available,
  designation: searchResult.designation,
  origin: searchResult.origin,
  references: searchResult.listings
    .filter((otherListing: any) => !MURFY_LISTING_SUPPLIER_NAMES.includes(otherListing.supplier_name))
    .map((otherListing: any) => ({
      supplier_name: otherListing.supplier_name,
      reference: otherListing.supplier_reference,
    })),
});

export const getMurfySkusFromSearch = async (filter: { reference?: string; type?: string | string[] }) => {
  if (!filter.reference) {
    return [];
  }
  const types = filter.type && (Array.isArray(filter.type) ? filter.type : [filter.type]);
  const searchResults = await searchSkuStock(filter.reference);
  const skus = searchResults.flatMap((result: any) =>
    result.listings
      .filter((listing: any) => listing.murfy_id != null)
      .filter((listing: any) => !types || types.includes(listing.type))
      .map((listing: any) => buildSkuFromMurfyListing(result, listing))
  );
  return sortSkus(skus);
};

export const useCancellationFees = (uuid: string) => {
  const { data: cancellationFees } = useQuery({
    type: "getList",
    resource: "cancellation-fees",
    payload: {
      sort: { field: "id", order: "DESC" },
      filter: {},
      uuid: uuid,
    },
  });
  return cancellationFees;
};

export const cancellationVisitWithFees = async (uuid: string, data: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/admin-cancel-with-fees/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(data);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const cancellationVisitWithoutFees = async (uuid: string) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/admin-cancel-without-fees/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const res = await fetch(url, { headers, method: "POST" });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const needsTeamLeaderCheck = async (uuid: string, data: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/repair-report-check/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(data);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const reportCheckedByLead = async (uuid: string, data: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/report-checked-by-lead/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(data);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const useTimeSlots = (
  employeeId: number | string | undefined,
  beginning: string,
  end: string,
  minimumCapacity: number = 1,
  type: string = "first_intervention"
) => {
  const { data: timeslots, loading: loadingTimeslots } = useQuery({
    type: "getList",
    resource: "timeslot",
    payload: {
      filter: {
        employee_id: employeeId,
        start_date: beginning,
        end_date: end,
        type: type,
        minimum_capacity: minimumCapacity,
      },
      sort: { field: "id", order: "DESC" },
    },
  });
  return { timeslots, loadingTimeslots };
};

export const useRepairmen = () => {
  const { data: repairmen } = useQuery({
    type: "getList",
    resource: "employees",
    payload: {
      filter: { role: "repairman" },
      sort: { field: "id", order: "DESC" },
    },
  });
  return repairmen;
};

export const proposalTimeslot = async (uuid: string, data: Record<string, any>, route: string = "suggest-timeslot") => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/${route}/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(data);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const getTeamLeader = async (
  employeeId: number | string
): Promise<{ data: Employee | null; status?: number }> => {
  return (await dataProvider.getOne(`common/employee/${employeeId}/team-leader`, {} as any)) as any;
};

export const useTeamLeader = (employee: Employee | null | undefined) => {
  const [teamLeader, setTeamLeader] = useState<Employee | null>(null);

  useEffect(() => {
    const fetchData = async (employeeId: number) => {
      const response = await getTeamLeader(employeeId);
      if ((response.status || 0) > 200) {
        setTeamLeader(null);
      } else {
        setTeamLeader(response.data);
      }
    };
    if (employee) fetchData(employee.id);
  }, [employee]);

  return teamLeader;
};

export const reschedule = async (uuid: string, data: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/admin-schedule/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(data);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const useRepairReportChecks = (customerFileId: number | string, state: string) =>
  useQuery({
    type: "getList",
    resource: "repair-report-checks",
    payload: {
      filter: {
        customer_file: customerFileId,
        state,
      },
      sort: { field: "id", order: "ASC" },
    },
  });

export const postComment = (payload: Record<string, any>) => {
  return dataProvider.create("comments", {
    data: payload,
  } as any);
};

export const updateRepairReportCheck = (repairReportCheckId: number | string, payload: Record<string, any>) => {
  return dataProvider.update("repair-report-checks", { id: repairReportCheckId, data: payload } as any);
};

export const convertToCustomerFile = async (payload: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/convert/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(payload);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const settleCreditNotes = async ({
  invoiceItems,
  commercialGestureAmount,
  creditNoteId,
}: {
  invoiceItems: number[] | undefined;
  commercialGestureAmount: number | null;
  creditNoteId: number | undefined;
}) => {
  const url = `${process.env.REACT_APP_API_URL}/payment/settlement/`;

  const token = localStorage.getItem("token");
  const headers = getHeaders(token);

  const response = await fetch(url, {
    headers: headers,
    method: "POST",
    body: JSON.stringify({
      credit_notes: [creditNoteId],
      itemized_credit_note: { items: invoiceItems, credit_note: creditNoteId },
      commercial_gesture_amount: commercialGestureAmount,
    }),
  });

  try {
    let data: any = await response.json();

    // Temporary conversion to handle new endpoint version
    data = data.coupon ? data.coupon : data;
    return { data: data, status: response.status };
  } catch {
    return { status: response.status };
  }
};

export const useLastVisitPreparation = (customerFileId: number | string) => {
  const { data: lastVisitPreparation } = useQuery({
    type: "getOne",
    resource: `murfy-erp/customer-files/${customerFileId}/last-visit-preparation`,
    payload: {},
  });
  return lastVisitPreparation;
};

export const updateVisitPreparation = (taskId: number | string, payload: Record<string, any>) => {
  return dataProvider.update("visit-preparations", { id: taskId, data: payload } as any);
};
export const reopeningCustomerFile = async (uuid: string) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/admin-reopening/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const res = await fetch(url, { headers, method: "POST" });
  try {
    const data = await res.json();
    return { data: data.data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const useHomeRepairZoneDeployedOnSevenOpteam = (postal_code: string) => {
  const { data: deployementStatus } = useQuery({
    type: "getOne",
    resource: "seven-opteam-deployed",
    payload: {
      postal_code: postal_code,
    },
  });
  return deployementStatus;
};

export const bookAppointment = async (uuid: string, payload: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/customer-file/${uuid}/book-appointment/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const res = await fetch(url, { headers, method: "POST", body: JSON.stringify(payload) });
  try {
    const data = await res.json();
    return { data: data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const postIncentive = async (payload: Record<string, any>) => {
  const url = `${process.env.REACT_APP_API_URL}/murfy-erp/incentives/`;
  const token = localStorage.getItem("token");

  const headers = getHeaders(token);
  const encodedData = JSON.stringify(payload);
  const res = await fetch(url, { headers, method: "POST", body: encodedData });
  try {
    const data = await res.json();
    return { data: data, status: res.status };
  } catch {
    return { status: res.status };
  }
};

export const useContractors = () => {
  const { data: contractors } = useQuery({
    type: "getList",
    resource: "contractors",
    payload: {},
  });
  return contractors;
};
